import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DollarSign, Plus, Home, Users, X, Loader2 } from 'lucide-react';
import Pagination from '../components/Pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RentCollections() {
  const [collections, setCollections] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProperty, setFilterProperty] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [stats, setStats] = useState({ total: 0, count: 0 });
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    entity_id: '', unit_id: '', tenant_name: '', payment_type: 'rent',
    amount: '', payment_date: new Date().toISOString().split('T')[0], payment_method: 'bank_transfer', reference_number: ''
  });

  useEffect(() => { fetchInitialData(); }, []);
  
  useEffect(() => { fetchCollections(); }, [pagination.page, filterProperty]);

  const fetchInitialData = async () => {
    try {
      const [propsRes, unitsRes] = await Promise.all([
        axios.get(`${API}/corporations`),
        axios.get(`${API}/units`)
      ]);
      setProperties(propsRes.data.filter(p => p.type === 'residential' || p.type === 'commercial' || p.property_type === 'residential' || p.property_type === 'commercial'));
      const unitData = unitsRes.data.data || unitsRes.data;
      setUnits(unitData);
    } catch (error) { console.error('Failed to fetch data:', error); }
  };

  const fetchCollections = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('payment_type', 'rent');
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      if (filterProperty) params.append('entity_id', filterProperty);
      
      const res = await axios.get(`${API}/collections/rent?${params}`);
      
      if (res.data.data) {
        setCollections(res.data.data);
        setPagination(prev => ({ ...prev, ...res.data.pagination }));
        setStats({
          total: res.data.data.reduce((sum, c) => sum + (c.amount || 0), 0),
          count: res.data.pagination.total
        });
      } else {
        const filtered = res.data.filter(c => c.payment_type === 'rent');
        setCollections(filtered);
        setStats({ total: filtered.reduce((sum, c) => sum + (c.amount || 0), 0), count: filtered.length });
      }
    } catch (error) { console.error('Failed to fetch collections:', error); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.limit, filterProperty]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/collections`, { ...formData, amount: parseFloat(formData.amount) });
      setShowModal(false);
      setFormData({ entity_id: '', unit_id: '', tenant_name: '', payment_type: 'rent', amount: '', payment_date: new Date().toISOString().split('T')[0], payment_method: 'bank_transfer', reference_number: '' });
      fetchCollections();
    } catch (error) { console.error('Failed to record payment:', error); }
  };

  const handleFilterChange = (value) => {
    setFilterProperty(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(amount || 0);

  return (
    <div className="space-y-6" data-testid="rent-collections-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Rent Payments</h1>
          <p className="text-slate-500">Collect rent from residential and commercial tenants • {stats.count.toLocaleString()} total</p>
        </div>
        <button onClick={() => setShowModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg" data-testid="collect-rent-btn"><Plus className="w-4 h-4" /> Collect Rent</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl p-5 text-white">
          <DollarSign className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-3xl font-bold">{formatCurrency(stats.total)}</p>
          <p className="text-emerald-100">{filterProperty ? 'Page Total (Property)' : 'Page Total'}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <Users className="w-6 h-6 text-indigo-500 mb-2" />
          <p className="text-2xl font-bold text-slate-900">{collections.length}</p>
          <p className="text-slate-500">Payments (This Page)</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <Home className="w-6 h-6 text-violet-500 mb-2" />
          <p className="text-2xl font-bold text-slate-900">{properties.length}</p>
          <p className="text-slate-500">Rental Properties</p>
        </div>
      </div>

      <div className="flex gap-4">
        <select value={filterProperty} onChange={(e) => handleFilterChange(e.target.value)} className="px-4 py-2 border border-slate-300 rounded-lg">
          <option value="">All Properties</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name} ({p.property_type})</option>)}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tenant</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Unit</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Property</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Method</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Reference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {collections.length === 0 ? (
                    <tr><td colSpan="7" className="px-4 py-12 text-center text-slate-500">No rent payments found</td></tr>
                  ) : (
                    collections.map((col) => (
                      <tr key={col.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm text-slate-600">{col.payment_date}</td>
                        <td className="px-4 py-3 text-sm font-medium text-slate-900">{col.tenant_name}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{col.unit_number}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{col.entity_name}</td>
                        <td className="px-4 py-3 text-sm text-slate-600 uppercase">{col.payment_method}</td>
                        <td className="px-4 py-3 text-right font-semibold text-emerald-600">{formatCurrency(col.amount)}</td>
                        <td className="px-4 py-3 text-xs text-slate-500 font-mono">{col.reference_number}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination pagination={pagination} onPageChange={(page) => setPagination(prev => ({ ...prev, page }))} />
          </>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Collect Rent Payment</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Property</label><select required value={formData.entity_id} onChange={(e) => setFormData({...formData, entity_id: e.target.value, unit_id: ''})} className="w-full px-3 py-2 border border-slate-300 rounded-lg"><option value="">Select property</option>{properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Unit</label><select required value={formData.unit_id} onChange={(e) => setFormData({...formData, unit_id: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg"><option value="">Select unit</option>{units.filter(u => u.entity_id === formData.entity_id).map(u => <option key={u.id} value={u.id}>{u.unit_number}</option>)}</select></div>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Tenant Name</label><input type="text" required value={formData.tenant_name} onChange={(e) => setFormData({...formData, tenant_name: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="Tenant name" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Amount</label><input type="number" required step="0.01" min="0" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="0.00" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Payment Date</label><input type="date" required value={formData.payment_date} onChange={(e) => setFormData({...formData, payment_date: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Payment Method</label><select value={formData.payment_method} onChange={(e) => setFormData({...formData, payment_method: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg"><option value="bank_transfer">Bank Transfer</option><option value="cheque">Cheque</option><option value="cash">Cash</option><option value="eft">EFT</option></select></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Reference #</label><input type="text" value={formData.reference_number} onChange={(e) => setFormData({...formData, reference_number: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" /></div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Collect Rent</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
